import React from 'react';
import { ICONS } from 'Components/utils/icon/icons';
import { useAppContext } from 'Context/ReactAppContext';
import { buildUrl, urlPattern } from 'Routes/routeBuilder';
import { t } from 'Src/i18n';
import signoutAction from 'Store/user-info/actions/signout';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { classnames } from 'Utils/classnames/classnames.utils';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { clearAllCacheFront } from 'Utils/graphql-client/cacheFront';
import * as pleUtils from 'Utils/ple-utils';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Icon } from 'Components/frameworks/icon/Icon';
type Props = {
  bookingId: string;
  closeMemberPanel?: () => void;
  stayId: string;
};
type Item = {
  dataTestId?: string;
  description?: string;
  hide?: boolean;
  icon: IconName;
  isExternalLink?: boolean;
  label: string;
  onClick?: () => void;
  target?: string;
  url: string | null | undefined;
};
export const QuickLoggedNav = ({ bookingId, closeMemberPanel, stayId }: Props) => {
  const { trackClickEvent } = useTrackEvent();
  const appContext = useAppContext();
  const isGsa = isFeatureFlipped(appContext)('isGsa');

  const signoutUrl = pleUtils.buildSignoutUrl({
    shouldRedirectToOidc: isGsa,
    locale: appContext.locale,
  });

  const items: Array<Item> = [
    {
      dataTestId: 'member-nav-quickdashboard-dashboard',
      icon: ICONS.NOTEBOOK,
      label: t('account.authenticated.dashboard'),
      onClick: () => {
        closeMemberPanel && closeMemberPanel();
        trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
          detail_click: 'memberSpace_dashboard',
        });
      },
      url: urlPattern('account'),
    },
    {
      dataTestId: 'member-nav-quickdashboard-details',
      hide: !bookingId || !stayId,
      icon: ICONS.DETAILS,
      label: t('account.authenticated.quickLogin.details'),
      onClick: () => {
        closeMemberPanel && closeMemberPanel();
        trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
          detail_click: 'memberSpace_bookingDetails',
        });
      },
      url: buildUrl('bookings', {
        bookingId,
        stayId,
      }),
    },
    {
      dataTestId: 'member-nav-quickdashboard-stepup',
      description: t('account.authenticated.quickLogin.signinDescription'),
      icon: ICONS.CONNECT,
      isExternalLink: true,
      label: t('account.authenticated.quickLogin.signin'),
      onClick: () => {
        closeMemberPanel && closeMemberPanel();
        trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
          detail_click: 'memberSpace_stepUp',
        });
        clearAllCacheFront();
      },
      url: urlPattern('stepup'),
    },
    {
      dataTestId: 'member-nav-quickdashboard-Signout',
      icon: ICONS.DISCONNECTION,
      isExternalLink: true,
      label: t('account.authenticated.quickLogin.signout'),
      onClick: () => {
        clearAllCacheFront();
        signoutAction();
        closeMemberPanel && closeMemberPanel();
        trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_NAV, {
          detail_click: 'memberSpace_logout',
        });
      },
      url: signoutUrl,
    },
  ];
  return (
    <nav>
      {items
        .filter((i: Item) => !i.hide)
        .map(
          (
            { dataTestId, description, icon, isExternalLink, label, onClick, target, url }: Item,
            index: number,
          ) => (
            <div
              className={classnames('p-10', {
                'border-t-1': index > 0,
              })}
              key={url}
            >
              {description && <p className="text-10 text-black">{description}</p>}
              <DynamicLink
                className="flex items-center gap-10 text-16 font-bold leading-tight text-black transition-color hocus:text-sienna"
                data-testid={dataTestId}
                isExternalLink={isExternalLink}
                queryParamsToIgnore={[
                  'coderesort',
                  'hasSaveOption',
                  'selectedProposalId',
                  'proposalIds',
                  'optionIds',
                ]}
                onClick={onClick}
                target={target}
                url={url}
              >
                {icon && <Icon name={icon} width="1.25rem" />}
                {label}
              </DynamicLink>
            </div>
          ),
        )}
    </nav>
  );
};
